// ** Redux Imports
import { Action } from 'redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  COUPON_HISTORY_SLICE_NAME,
  GET_COUPON_HISTORY_BY_USER_ID_ACTION,
  ISSUE_COUPON_ACTION,
  UPDATE_COUPON_STATUS_ACTION
} from './actions'
import type { AxiosInstance } from 'axios'
import { GygCouponHistoryType } from '@/src/types/apps/gygLoyaltyHistoryTypes'

type AxiosMiddlewareExtras = {
  httpClient: AxiosInstance
}

export type GygCouponHistoryStateType = {
  gygCouponHistory: GygCouponHistoryType[]
  getCouponHistoryPending: boolean
  issueCouponPending: boolean
  issueCouponError: any
  issueCouponSuccess: string | undefined
  updateCouponStatusPending: boolean
}

interface Redux {
  getState: () => any
  dispatch: ThunkDispatch<any, AxiosMiddlewareExtras, Action<string>>
  extra: AxiosMiddlewareExtras
}

interface CouponHistoryResponse {
  data: GygCouponHistoryType[]
}

export interface IssueCouponPayload {
  userId: string
  data: {
    masterCouponId: string
    hook: string
    note?: string
  }
}

export interface UpdateCouponStatusPayload {
  userId: string
  couponId: string
  data: {
    isActive: boolean
  }
}

export const getCouponHistoryByUserId = createAsyncThunk<CouponHistoryResponse, string, Redux>(
  GET_COUPON_HISTORY_BY_USER_ID_ACTION,
  async (userId: string, { extra }) => {
    const response = await extra.httpClient(`users/${userId}/coupons`)

    return response.data
  }
)

export const issueCoupon = createAsyncThunk<any, IssueCouponPayload, Redux>(
  ISSUE_COUPON_ACTION,
  async ({ userId, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/${userId}/coupons`, { method: 'POST', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error.message)
    }
  }
)

export const updateCouponStatus = createAsyncThunk<any, UpdateCouponStatusPayload, Redux>(
  UPDATE_COUPON_STATUS_ACTION,
  async ({ userId, couponId, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`users/${userId}/coupons/${couponId}`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error.message)
    }
  }
)

const initialState: GygCouponHistoryStateType = {
  gygCouponHistory: [],
  getCouponHistoryPending: false,
  issueCouponPending: false,
  issueCouponError: null,
  issueCouponSuccess: undefined,
  updateCouponStatusPending: false
}

export const gygCouponHistorySlice = createSlice({
  name: COUPON_HISTORY_SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getCouponHistoryByUserId.fulfilled, (state, action) => {
        state.getCouponHistoryPending = false
        state.gygCouponHistory = action.payload.data
      })
      .addCase(getCouponHistoryByUserId.pending, state => {
        state.getCouponHistoryPending = true
      })
      .addCase(getCouponHistoryByUserId.rejected, state => {
        state.getCouponHistoryPending = false
      })

      .addCase(issueCoupon.fulfilled, state => {
        state.issueCouponPending = false
        state.issueCouponSuccess = 'Signup Reward Issued'
        state.issueCouponError = null
      })
      .addCase(issueCoupon.pending, state => {
        state.issueCouponPending = true
        state.issueCouponError = null
        state.issueCouponSuccess = undefined
      })
      .addCase(issueCoupon.rejected, (state, action) => {
        state.issueCouponPending = false
        state.issueCouponSuccess = undefined
        state.issueCouponError = action.payload ?? action.error
      })

      .addCase(updateCouponStatus.fulfilled, (state, action) => {
        state.updateCouponStatusPending = false

        const updatedCoupon = action.payload.data ?? action.payload
        const index = state.gygCouponHistory.findIndex(coupon => coupon.id === updatedCoupon.id)

        if (index !== -1) {
          state.gygCouponHistory[index] = { ...state.gygCouponHistory[index], ...updatedCoupon }
        }
      })
      .addCase(updateCouponStatus.pending, state => {
        state.updateCouponStatusPending = true
      })
      .addCase(updateCouponStatus.rejected, state => {
        state.updateCouponStatusPending = false
      })
  }
})

export default gygCouponHistorySlice.reducer
